import type { ApiRequestParams, ApiResponse, DataTableColumn } from "@/components/DataTable/types";
import { exportToCSV, exportToExcel } from "@/components/DataTable/utils";
import { UserType, VendorType } from "@/interfaces";
import { useGetVendorUsersByCompanyStats, useGetVendorUsersByCompaniesStats } from "@/services/vendorUserService";
import vendorService, { useGetVendorsByCompanyId, useGetVendorsByCompanyIds } from "@/services/vendorService";
import { usePermissions } from "@/lib/permissionUtils";
import { useUserStore } from "@/stores/userStore";
import { Button, Card, Modal, message } from "antd";
import { Briefcase, Plus, Users } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import CreateVendorUserModal from "./CreateVendorUserModal";
import EditVendorUserModal from "./EditVendorUserModal";
import RequestVendorModal from "./RequestVendorModal";
import { VendorTable } from "./VendorTable";
import ViewVendorModal from "./ViewVendorModal";

const VendorManagement = () => {
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [requestVendorModalOpen, setRequestVendorModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<VendorType | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [, setTableParams] = useState<ApiRequestParams>({ page: 1, pageSize: 10 });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [selectedUserVendorId, setSelectedUserVendorId] = useState<string | null>(null);

  const currentCompany = useUserStore((state) => state.currentCompany);
  const user = useUserStore((state) => state.user);
  const isFinance = user?.role === "Finance";
  const financeCompanyIds = useMemo(() => (isFinance ? (user?.companies?.map((c) => c.id) ?? []) : []), [isFinance, user?.companies]);
  const companyId = currentCompany?.id;
  const { canEdit } = usePermissions();
  const hasEditAccess = canEdit("vendorManagement");

  const { data: singleCompanyStats, refetch: refetchSingleCompanyStats } = useGetVendorUsersByCompanyStats(isFinance ? undefined : companyId);
  const { data: financeStats, refetch: refetchFinanceStats } = useGetVendorUsersByCompaniesStats(financeCompanyIds);
  const vendorStats = isFinance ? financeStats : singleCompanyStats;

  const { data: singleCompanyVendors, refetch: refetchSingleCompanyVendors } = useGetVendorsByCompanyId(isFinance ? null : (companyId ?? null));
  const { data: financeVendors, refetch: refetchFinanceVendors } = useGetVendorsByCompanyIds(financeCompanyIds);
  const vendorData = isFinance ? financeVendors : singleCompanyVendors;
  const vendors = vendorData?.data ?? [];

  const refreshPageData = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
    refetchSingleCompanyStats();
    refetchFinanceStats();
    refetchSingleCompanyVendors();
    refetchFinanceVendors();
  }, [refetchSingleCompanyStats, refetchFinanceStats, refetchSingleCompanyVendors, refetchFinanceVendors]);

  const handleView = (vendor: VendorType) => {
    setSelectedVendor(vendor);
    setViewModalOpen(true);
  };

  const handleEdit = (vendor: VendorType) => {
    setSelectedVendor(vendor);
    setEditModalOpen(true);
  };

  const handleToggleActive = (vendor: VendorType) => {
    const activating = !vendor.isActive;
    Modal.confirm({
      title: `${activating ? "Activate" : "Deactivate"} Vendor`,
      content: `Are you sure you want to ${activating ? "activate" : "deactivate"} "${vendor.vendorName}"?`,
      okText: activating ? "Activate" : "Deactivate",
      okButtonProps: { danger: !activating },
      onOk: async () => {
        try {
          await vendorService.updateStatus(vendor.id, activating);
          message.success(`Vendor ${activating ? "activated" : "deactivated"} successfully`);
          refreshPageData();
        } catch (error: any) {
          message.error(error?.response?.data?.message || `Failed to ${activating ? "activate" : "deactivate"} vendor`);
        }
      },
    });
  };

  const handleEditUser = (vendor: VendorType, user: UserType) => {
    setSelectedUserVendorId(vendor.id);
    setSelectedUser(user);
    setEditUserModalOpen(true);
  };

  const vendorCards = [
    {
      title: "Total Vendor Users",
      value: vendorStats?.totalUsers || 0,
      subtitle: "All vendor users",
      icon: <Users className="text-blue-400 w-5 h-5" />,
    },
    {
      title: "New Users",
      value: vendorStats?.newUsers || 0,
      subtitle: "Last 30 days",
      icon: <Plus className="text-green-400 w-5 h-5" />,
    },
    {
      title: "Active Users",
      value: vendorStats?.activeUsers || 0,
      subtitle: "Currently active",
      icon: <Briefcase className="text-purple-400 w-5 h-5" />,
    },
  ];

  const fetchVendors = useCallback(
    async (params: ApiRequestParams): Promise<ApiResponse<VendorType>> => {
      let filtered = [...vendors];

      if (params.search) {
        const s = params.search.toLowerCase();
        filtered = filtered.filter(
          (v) => v.vendorName?.toLowerCase().includes(s) || v.displayName?.toLowerCase().includes(s),
        );
      }

      if (params.filters?.isActive !== undefined && params.filters.isActive !== "") {
        const activeVal = params.filters.isActive === "true" || params.filters.isActive === true;
        filtered = filtered.filter((v) => v.isActive === activeVal);
      }

      const page = params.page || 1;
      const pageSize = params.pageSize || 10;
      const start = (page - 1) * pageSize;

      return {
        data: filtered.slice(start, start + pageSize),
        pagination: {
          total: filtered.length,
          page,
          pageSize,
          totalPages: Math.ceil(filtered.length / pageSize) || 1,
        },
      };
    },
    [vendors],
  );

  const exportColumns = useMemo<DataTableColumn<Record<string, unknown>>[]>(
    () => [
      {
        key: "vendorName",
        title: "Vendor Name",
        dataIndex: "vendorName",
        exportValue: (value: unknown, record: Record<string, unknown>) =>
          (record.displayName as string) || (value as string) || (record.vendorLegalName as string),
      },
      {
        key: "users",
        title: "Users",
        dataIndex: "users",
        exportValue: (value: unknown) => String((value as unknown[])?.length ?? 0),
      },
      {
        key: "approvalStatus",
        title: "Approval",
        dataIndex: "approvalStatus",
      },
      {
        key: "isActive",
        title: "Status",
        dataIndex: "isActive",
        exportValue: (value: unknown) => ((value as boolean) ? "Active" : "Inactive"),
      },
      {
        key: "createdAt",
        title: "Created",
        dataIndex: "createdAt",
      },
    ],
    [],
  );

  const handleExport = useCallback(
    async (type: "csv" | "excel") => {
      const rows = vendors as unknown as Record<string, unknown>[];
      if (type === "csv") {
        exportToCSV(rows, exportColumns, "vendors-export");
      } else {
        exportToExcel(rows, exportColumns, "vendors-export");
      }
    },
    [vendors, exportColumns],
  );

  return (
    <div className="h-full p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Vendor Management</h1>
            <p className="text-gray-600">Manage vendors and their users.</p>
          </div>
          {hasEditAccess && (
            <div className="flex gap-2">
              <Button onClick={() => setRequestVendorModalOpen(true)}>Create Vendor</Button>
              <Button type="primary" icon={<Plus className="w-4 h-4" />} onClick={() => setAddUserModalOpen(true)}>
                Add Vendor User
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {vendorCards.map((card, index) => (
          <Card key={index} className="shadow-sm" bodyStyle={{ padding: 24 }}>
            <div className="flex justify-between flex-col">
              <div className="flex justify-between mb-8 items-center">
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <div className="p-2 bg-blue-50 rounded-lg">
                  <span className="text-xl">{card.icon}</span>
                </div>
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">{card.value}</p>
                <p className="text-xs text-gray-500 mt-1">{card.subtitle}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Vendor Table */}
      <Card className="shadow-sm">
        <VendorTable
          onView={handleView}
          onEdit={handleEdit}
          onToggleActive={handleToggleActive}
          onEditUser={handleEditUser}
          fetchData={fetchVendors}
          onTableChange={setTableParams}
          onExport={handleExport}
          refreshKey={refreshKey}
          canEdit={hasEditAccess}
          showCompanyColumn={isFinance}
        />
      </Card>

      {/* Modals */}
      {addUserModalOpen && (
        <CreateVendorUserModal
          open={addUserModalOpen}
          onCancel={() => setAddUserModalOpen(false)}
          onSuccess={() => {
            setAddUserModalOpen(false);
            refreshPageData();
          }}
        />
      )}

      {requestVendorModalOpen && (
        <RequestVendorModal
          open={requestVendorModalOpen}
          onCancel={() => setRequestVendorModalOpen(false)}
          onSuccess={() => {
            setRequestVendorModalOpen(false);
            refreshPageData();
          }}
        />
      )}

      {viewModalOpen && selectedVendor && (
        <ViewVendorModal open={viewModalOpen} vendor={selectedVendor} onCancel={() => setViewModalOpen(false)} />
      )}

      {editModalOpen && selectedVendor && (
        <RequestVendorModal
          open={editModalOpen}
          vendor={selectedVendor}
          onCancel={() => setEditModalOpen(false)}
          onSuccess={() => {
            setEditModalOpen(false);
            refreshPageData();
          }}
        />
      )}

      {editUserModalOpen && selectedUser && selectedUserVendorId && (
        <EditVendorUserModal
          open={editUserModalOpen}
          vendorId={selectedUserVendorId}
          user={selectedUser}
          onCancel={() => setEditUserModalOpen(false)}
          onSuccess={() => {
            setEditUserModalOpen(false);
            refreshPageData();
          }}
        />
      )}
    </div>
  );
};

export default VendorManagement;
